/**
 * main-menu.ts — full-screen DOM overlay for the main menu (v2M1).
 *
 * Per UI_UX §9.1. Four buttons over a dark background: New Game, Load Game
 * (disabled until v2M2), Settings (stubbed until v2M4), Exit.
 *
 * Pure DOM — no canvas interaction. Palette pulled from GRAPHICS §2.1 so
 * it sits cleanly above the blanked canvas and harmonises with the in-game
 * HUD when the player transitions into a session.
 *
 * The menu owns its overlay element. On destroy(), the element is removed
 * from the DOM so the gameplay canvas becomes the active render surface.
 *
 * v2M7.8b — adds a background `<canvas>` underneath the buttons that
 * blits the cached procedural nebula key art (`getMainMenuKeyArt`).
 * Headless test envs return null from the accessor, in which case the
 * existing flat `#05060A` overlay background remains as the visible
 * fallback.
 */

import { getMainMenuKeyArt, onKeyArtSwap } from '../render/backdrop';

export interface MainMenuCallbacks {
  onNewGame(): void;
  onLoadGame(): void;
  onSettings(): void;
  onExit(): void;
}

export interface MainMenuHandle {
  destroy(): void;
}

export function mountMainMenu(
  parent: HTMLElement,
  callbacks: MainMenuCallbacks,
): MainMenuHandle {
  const overlay = document.createElement('div');
  overlay.id = 'main-menu-overlay';
  Object.assign(overlay.style, {
    position: 'fixed',
    inset: '0',
    background: '#05060A',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: '1000',
    fontFamily:
      'Inter, system-ui, -apple-system, "Segoe UI", sans-serif',
    color: '#D7DEEA',
  } as Partial<CSSStyleDeclaration>);

  // v2M7.8b: nebula key-art canvas. First child of the overlay so it
  // paints behind everything else without needing explicit z-indices on
  // each menu item. `position: absolute` + `inset: 0` takes it out of
  // the flex flow entirely, so the centred title / button column remain
  // perfectly centred. Resize handler keeps the bitmap matched to the
  // window so the nebula doesn't pixel-stretch awkwardly on rescale.
  // MFix13.2a — restructured to handle the first-mount race. Original
  // path captured `getMainMenuKeyArt()` into a `const` at line 60 and
  // bound `repaint` only to `window.resize`. When `main.ts` fires
  // `tryLoadCommissionedBackdrops()` async and immediately mounts the
  // menu, the commissioned PNG resolves *after* mount — `keyArt` was
  // null at mount, `bgCanvas` was never created, and the swap that
  // landed seconds later went unnoticed. Now we re-read `getMainMenuKeyArt()`
  // inside `repaint` and lazily build / fill the canvas on first non-
  // null read, then subscribe to `onKeyArtSwap` so a late-arriving
  // PNG triggers a repaint.
  let bgCanvas: HTMLCanvasElement | null = null;
  let onResize: (() => void) | null = null;
  let unsubscribeKeyArtSwap: (() => void) | null = null;
  const repaint = (): void => {
    const keyArt = getMainMenuKeyArt();
    if (!keyArt) return; // procedural cache miss + no commissioned art yet
    if (!bgCanvas) {
      bgCanvas = document.createElement('canvas');
      Object.assign(bgCanvas.style, {
        position: 'absolute',
        inset: '0',
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
        // Explicit zIndex 0 so the canvas paints below the foreground
        // flex items below (which get zIndex 1). Without this, modern
        // Chromium paints the position:absolute canvas AFTER position:static
        // siblings per CSS painting order — the canvas covers the menu
        // and the buttons stay clickable (pointerEvents:none) but invisible.
        zIndex: '0',
      } as Partial<CSSStyleDeclaration>);
      // Prepend so the canvas paints behind any foreground items appended
      // earlier in this function's lifetime (defensive — currently the
      // canvas creation runs before the title/buttons, but a late-mount
      // path could reverse that order).
      overlay.insertBefore(bgCanvas, overlay.firstChild);
    }
    const w = window.innerWidth;
    const h = window.innerHeight;
    bgCanvas.width = w;
    bgCanvas.height = h;
    const bgCtx = bgCanvas.getContext('2d');
    if (!bgCtx) return;
    // Fit-to-cover: scale the nebula so the whole window is filled,
    // cropping centred. Avoids letter-boxing on extreme aspect ratios.
    const sourceAspect = keyArt.width / keyArt.height;
    const screenAspect = w / h;
    let dw: number;
    let dh: number;
    let dx: number;
    let dy: number;
    if (screenAspect > sourceAspect) {
      dw = w;
      dh = w / sourceAspect;
      dx = 0;
      dy = (h - dh) / 2;
    } else {
      dh = h;
      dw = h * sourceAspect;
      dx = (w - dw) / 2;
      dy = 0;
    }
    bgCtx.drawImage(keyArt, dx, dy, dw, dh);
  };
  repaint();
  onResize = repaint;
  window.addEventListener('resize', onResize);
  // Late-arriving commissioned PNG → re-paint with the swapped canvas.
  unsubscribeKeyArtSwap = onKeyArtSwap(repaint);

  const title = document.createElement('div');
  title.textContent = 'STRATEGOS UNIVERSALIS';
  Object.assign(title.style, {
    fontSize: '42px',
    letterSpacing: '0.15em',
    fontWeight: '600',
    marginBottom: '8px',
    position: 'relative',
    zIndex: '1',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(title);

  const subtitle = document.createElement('div');
  subtitle.textContent = 'a browser 4X';
  Object.assign(subtitle.style, {
    fontSize: '14px',
    color: '#7E8799',
    marginBottom: '48px',
    letterSpacing: '0.08em',
    position: 'relative',
    zIndex: '1',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(subtitle);

  const column = document.createElement('div');
  Object.assign(column.style, {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    minWidth: '280px',
    position: 'relative',
    zIndex: '1',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(column);

  const makeButton = (
    label: string,
    onClick: () => void,
    opts: { disabled?: boolean; tooltip?: string } = {},
  ): HTMLButtonElement => {
    const btn = document.createElement('button');
    btn.textContent = label;
    btn.type = 'button';
    btn.disabled = opts.disabled === true;
    if (opts.tooltip) btn.title = opts.tooltip;
    Object.assign(btn.style, {
      padding: '14px 24px',
      background: '#0E131C',
      color: opts.disabled ? '#505868' : '#D7DEEA',
      border: '1px solid #2A3344',
      borderRadius: '2px',
      fontFamily: 'inherit',
      fontSize: '16px',
      letterSpacing: '0.06em',
      cursor: opts.disabled ? 'not-allowed' : 'pointer',
      textAlign: 'left',
      transition: 'background 80ms ease, border-color 80ms ease',
    } as Partial<CSSStyleDeclaration>);
    if (!opts.disabled) {
      btn.addEventListener('mouseenter', () => {
        btn.style.background = '#172031';
        btn.style.borderColor = '#3F4C66';
      });
      btn.addEventListener('mouseleave', () => {
        btn.style.background = '#0E131C';
        btn.style.borderColor = '#2A3344';
      });
      btn.addEventListener('click', onClick);
    }
    column.appendChild(btn);
    return btn;
  };

  // v4M1.2 — per-button tooltips (UI_UX §6).
  makeButton('New Game', callbacks.onNewGame, {
    tooltip: 'Configure a fresh galaxy and empire roster, then launch into play.',
  });
  // Load Game always enabled — the picker handles the no-saves-yet case
  // by greying out the Load buttons next to empty slots (v2M2).
  makeButton('Load Game', callbacks.onLoadGame, {
    tooltip: 'Open the save-slot picker to resume an earlier game.',
  });
  makeButton('Settings', callbacks.onSettings, {
    tooltip: 'Audio / Gameplay settings overlay.',
  });
  makeButton('Exit', callbacks.onExit, {
    tooltip: 'Close the application.',
  });

  const footer = document.createElement('div');
  footer.textContent = 'v1 MVP · build dev';
  Object.assign(footer.style, {
    position: 'absolute',
    bottom: '12px',
    right: '16px',
    fontSize: '11px',
    color: '#505868',
    letterSpacing: '0.04em',
    zIndex: '1',
  } as Partial<CSSStyleDeclaration>);
  overlay.appendChild(footer);

  parent.appendChild(overlay);

  return {
    destroy(): void {
      if (onResize) window.removeEventListener('resize', onResize);
      if (unsubscribeKeyArtSwap) unsubscribeKeyArtSwap();
      overlay.remove();
    },
  };
}
